import React, { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { Link as RouterLink } from 'react-router-dom';
import PostContent from './PostContent';
import PostContextMenu from './PostContextMenu';
import ShareButton from './ShareButton';
import type { CrosspostOriginal } from './CrosspostEmbed';
import { trimLink, prettifyLink, fixUrl } from '../utils/formatUrl';
import TimeAgo from 'react-timeago';
import ReactionBar from './ReactionBar';
import AwardTray from './AwardTray';
import AwardBadges from './AwardBadges';
import UserAvatar from './UserAvatar';
import UserName from './UserName';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import TrueFalseDonut from './TrueFalseDonut';
import HoverPreviewCard from './HoverPreviewCard';
import { Paper, Typography, useMediaQuery, Link, Button, Box, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { postGlassSx } from '../theme/glass';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import CallMadeIcon from '@mui/icons-material/CallMade';
import { Capacitor } from '@capacitor/core';
import VideoPoster from './VideoPoster';
import type { UserState, MediaItem, LinkPreview, MediaLayout, ReactionMap, ReactionEntry } from '../types/common';

interface HerdInfo {
  herdName: string;
}

interface UserInfo {
  id: string;
  username: string;
  avatar?: { exists?: boolean; imageLink?: string };
}

interface PostShape {
  id: string;
  title: string;
  textSubmission?: string;
  media?: MediaItem[];
  links?: LinkPreview[];
  mediaLayout?: MediaLayout;
  herd: HerdInfo;
  author: UserInfo;
  upvotedBy: string[];
  downvotedBy: string[];
  reactions?: ReactionMap;
  reactionEntries?: ReactionEntry[];
  trueReactionCount?: number;
  falseReactionCount?: number;
  crosspostOf?: CrosspostOriginal | null;
  crosspostNote?: string;
  crosspostMeta?: { authorName?: string; herdName?: string };
  crosspostCount?: number;
  pointsCount: number;
  commentCount: number;
  createdAt: string;
  updatedAt: string;
}

// Vote thunks are retained (dormant) so feed callers keep compiling while the
// upvote/downvote UI is hidden; PostCard no longer renders vote controls.
type VoteThunk = (id: string, a: string[], b: string[]) => unknown;
// `held` = the user already has this reaction, so the thunk removes it.
type ReactionThunk = (id: string, reactionKey: string, held: boolean) => unknown;

interface PostCardProps {
  post: PostShape;
  /**
   * Slice-specific vote thunk creators (postReducer / herdPageReducer /
   * searchReducer / userPageReducer). Dispatched internally.
   */
  toggleUpvote: VoteThunk;
  toggleDownvote: VoteThunk;
  /** Slice-specific reaction thunk creator; reactions UI is read-only without it. */
  toggleReaction?: ReactionThunk;
  /** `compact` = horizontal summary card; `expanded` = full content card. */
  variant?: 'compact' | 'expanded';
}

const PostCard: FC<PostCardProps> = ({ post, toggleUpvote, toggleDownvote, toggleReaction, variant = 'compact' }) => {
  const postRef = useRef<HTMLDivElement | null>(null);
  const impressionTracked = useRef(false);

  const {
    id,
    title,
    textSubmission,
    media = [],
    links = [],
    mediaLayout,
    herd,
    author,
    reactions = {},
    reactionEntries = [],
    trueReactionCount = 0,
    falseReactionCount = 0,
    crosspostOf,
    crosspostCount = 0,
    commentCount,
    createdAt,
    updatedAt,
  } = post;

  const isExpanded = variant === 'expanded';
  // A share carries the original as an in-app link in its body (auto-embedded by
  // PostContent). crosspostOf is still populated so compact cards can borrow the
  // original's thumbnail and we can show "Shared N times".
  const original = (crosspostOf as CrosspostOriginal | null | undefined) || null;

  useEffect(() => {
    const node = postRef.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !impressionTracked.current) {
          impressionTracked.current = true;
          // @ts-expect-error Posthog may be injected globally.
          window.posthog?.capture?.('post_impression', { post_id: id, title });
        }
      },
      { threshold: 0.5 },
    );

    if (node) observer.observe(node);
    return () => {
      if (node) observer.unobserve(node);
    };
  }, [id, title]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) || Capacitor.getPlatform() !== 'web';
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.user as UserState | null);
  const glassSettings = useAppSelector(state => state.glassSettings);
  // Null when the effect is off — the solid card recipe below then applies.
  const glass = postGlassSx(theme.tokens, theme.palette.mode, glassSettings);

  const [awardAnchor, setAwardAnchor] = useState<HTMLElement | null>(null);
  const awardSummary = (post as any).awardSummary as { key: string; count: number }[] | undefined;
  // Snapshotted when the post was written — deliberately NOT the author's
  // current badge, so old posts keep what they were made with.
  const authorBadge = (post as any).authorBadge as string | null | undefined;

  const handleReactionToggle = (reactionKey: string, held: boolean) => {
    if (!user || !toggleReaction) return;
    dispatch(toggleReaction(id, reactionKey, held) as never);
  };

  const firstMedia = media[0] || original?.media?.[0];
  const firstLink = links[0];
  const formattedLink = firstLink ? trimLink(prettifyLink(firstLink.url), 30) : '';

  const authorAvatar = (size: number) => (
    <UserAvatar user={author as any} size={size} ring="brand" link />
  );

  // Design subline (.post-head .hd): 12.5px, muted text-3.
  const metaLine = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: isExpanded ? 0 : 0.5, mb: isExpanded ? 1 : 0.5 }}>
      <Typography
        variant="caption"
        sx={{ color: theme.tokens.colors.text3, fontSize: theme.tokens.typography.sizes.postMeta }}
      >
        <HoverPreviewCard type="user" name={author.username}>
          <UserName
            username={author.username}
            prefix="@"
            badgeKey={authorBadge}
            link
            sx={{ color: theme.tokens.colors.text3, fontSize: theme.tokens.typography.sizes.postMeta }}
          />
        </HoverPreviewCard>{' '}
        •{' '}
        <HoverPreviewCard type="herd" name={herd.herdName}>
          <Link
            component={RouterLink}
            to={`/herd/${herd.herdName}`}
            sx={{ color: theme.tokens.colors.text3, fontSize: theme.tokens.typography.sizes.postMeta }}
          >
            {herd.herdName}
          </Link>
        </HoverPreviewCard>{' '}
        • <TimeAgo date={new Date(createdAt)} />
        {createdAt !== updatedAt && ' • edited'}
      </Typography>
    </Box>
  );

  // Guide `.pill-act`: compact mono pill actions with a state-layer hover.
  const pillActSx = {
    textTransform: 'none',
    fontFamily: theme.tokens.typography.monoFontFamily,
    fontWeight: theme.tokens.typography.fontWeightMedium,
    fontSize: '11.5px',
    color: theme.tokens.colors.text2,
    borderRadius: 'var(--sh-full)',
    padding: '9px 14px',
    minHeight: 0,
    '&:hover': {
      color: theme.tokens.colors.textPrimary,
      backgroundColor: `rgba(${theme.tokens.colors.stateLayer}, 0.08)`,
    },
  } as const;

  const actionButtons = (
    <>
      <Button
        startIcon={<ModeCommentOutlinedIcon />}
        sx={pillActSx}
        component={RouterLink}
        to={`/comments/${id}`}
        size={isMobile ? 'small' : 'medium'}
      >
        {commentCount} comments
      </Button>
      <ShareButton id={id} title={title} pillSx={pillActSx} size={isMobile ? 'small' : 'medium'} />
      {crosspostCount > 0 && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: theme.tokens.colors.text3,
            fontFamily: theme.tokens.typography.monoFontFamily,
            fontSize: '11.5px',
          }}
        >
          <CallMadeIcon sx={{ fontSize: 13 }} /> Shared {crosspostCount} {crosspostCount === 1 ? 'time' : 'times'}
        </Box>
      )}
    </>
  );

  // Design (.post-more): the context menu lives at the far right of the header
  // row. Shown to any signed-in reader — it carries hide/report as well as the
  // author's edit/delete, and decides internally which of those to offer.
  const contextMenu = user && (
    <Box sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
      <PostContextMenu id={id} title={title} authorId={author.id} />
    </Box>
  );

  // Design (.post-head .nm): title line is sans, 700, 14.5px.
  const titleSx = {
    fontFamily: theme.tokens.typography.sansFontFamily,
    fontSize: theme.tokens.typography.sizes.postTitle,
    fontWeight: theme.tokens.typography.fontWeightBold,
    color: theme.tokens.colors.textHeading,
  };

  // -------- Expanded variant --------
  if (isExpanded) {
    return (
      <Paper
        ref={postRef}
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          width: '100%',
          maxWidth: theme.tokens.layout.contentMaxWidth,
          margin: '0 auto',
          overflow: 'clip',
          borderRadius: 'var(--sh-xl)',
          p: 2,
          mb: 3,
          backgroundColor: theme.palette.background.post,
          border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.cardBorder}`,
          boxShadow: theme.palette.mode === 'light' ? theme.tokens.shadows.expandedCardLight : theme.tokens.shadows.none,
          transition: `box-shadow var(--dur-m3) var(--ease-m3)`,
          '&:hover': {
            boxShadow: theme.palette.mode === 'light' ? theme.tokens.shadows.expandedCardHoverLight : theme.tokens.shadows.cardHoverDark,
          },
          ...glass,
        }}
      >
        <Box sx={{ mb: 2, zIndex: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
            {authorAvatar(40)}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h4" sx={{ ...titleSx, mb: 0.5 }}>
                {title}
              </Typography>
              {metaLine}
            </Box>
            {contextMenu}
          </Box>
          <PostContent
            textSubmission={textSubmission}
            media={media}
            links={links}
            mediaLayout={mediaLayout}
            mediaMaxHeight={isMobile ? theme.tokens.layout.mobileMediaPreviewHeight : undefined}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
          {actionButtons}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <ReactionBar
            user={user}
            reactions={reactions}
            reactionEntries={reactionEntries}
            onToggle={handleReactionToggle}
            size={isMobile ? 'small' : 'medium'}
          />
          {user && (
            <Tooltip title="Give an award">
              <IconButton
                size="small"
                aria-label="Give an award"
                onClick={(event) => setAwardAnchor(event.currentTarget)}
                sx={{ color: theme.tokens.colors.text3 }}
              >
                <EmojiEventsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ ml: 'auto', display: 'flex' }}>
            <TrueFalseDonut trueCount={trueReactionCount} falseCount={falseReactionCount} size={20} />
          </Box>
        </Box>
        <AwardBadges summary={awardSummary} />
        <AwardTray anchorEl={awardAnchor} onClose={() => setAwardAnchor(null)} postId={id} />
      </Paper>
    );
  }

  // -------- Compact variant --------
  return (
    <Paper
      ref={postRef}
      variant="outlined"
      sx={{
        display: 'flex',
        position: 'relative',
        width: 'auto',
        overflow: 'clip',
        borderRadius: 'var(--sh-xl)',
        p: 1,
        mb: 2,
        backgroundColor: theme.palette.background.post,
        border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.cardBorder}`,
        boxShadow: theme.tokens.shadows.card,
        transition: `box-shadow var(--dur-m3) var(--ease-m3)`,
        '&:hover': {
          boxShadow: theme.tokens.shadows.cardHover,
          '& .post-type-icon': { opacity: 0.16, transform: theme.tokens.animations.hoverScaleStrong },
        },
        ...glass,
      }}
    >
      <Box sx={{ p: 1.25, pb: 0, flexGrow: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          {authorAvatar(40)}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h4" sx={{ ...titleSx, mr: 0.6 }}>
              {title}{' '}
              {firstLink && (
                <Typography variant="caption" color="primary" component="span" sx={{ ml: 1 }}>
                  <Link href={fixUrl(firstLink.url)} target="_blank" rel="noopener" sx={{ ml: 0.5 }}>
                    {formattedLink}
                    <OpenInNewIcon fontSize="inherit" />
                  </Link>
                </Typography>
              )}
            </Typography>

            {metaLine}
          </Box>
          {contextMenu}
        </Box>

        <Box sx={{ display: 'flex', mt: 1, alignItems: 'center', gap: 1 }}>
          {actionButtons}
          <AwardBadges summary={awardSummary} size="small" />
          <Box sx={{ ml: 'auto', pr: 0.5 }}>
            <TrueFalseDonut trueCount={trueReactionCount} falseCount={falseReactionCount} size={16} />
          </Box>
        </Box>
      </Box>
      {firstMedia && (
        <Box
          sx={{
            position: 'relative',
            width: 'var(--layout-thumbnail)',
            height: 'var(--layout-thumbnail)',
            borderRadius: 'var(--sh-lg)',
            overflow: 'hidden',
            alignSelf: 'center',
            flexShrink: 0,
            border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.glassLine}`,
            backgroundColor: theme.palette.background.default,
          }}
        >
          {firstMedia.type === 'Video' ? (
            <VideoPoster
              url={firstMedia.url}
              duration={firstMedia.duration}
              // Renders in a 72px (--layout-thumbnail) tile.
              width={240}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <img src={firstMedia.url} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}
          {media.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 2,
                right: 2,
                backgroundColor: 'rgba(0,0,0,0.65)',
                color: '#fff',
                fontFamily: theme.tokens.typography.monoFontFamily,
                fontSize: '10px',
                px: 0.5,
                borderRadius: '4px',
              }}
            >
              +{media.length - 1}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default PostCard;
